import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  AllowedMarkChannels,
  EncField,
  EncSeries,
  Encoding,
  ExprFrame,
  MarkChannel,
  MarkType,
  MarkTypeOptions,
  MarksSummary,
  SortType,
} from '@mode-vegas/vegas';
import { trackByFactory } from '@mode/capra';
import { SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers, RenderDataModel, SwitchSchemaModel } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import { BaselineChannelComponent } from '../channels/baseline-channel.component';
import { ChannelComponent } from '../channels/channel.component';
import { ColorChannelComponent } from '../channels/color-channel.component';
import { TextChannelComponent } from '../channels/text-channel.component';
import { LayerTypeSelectorComponent } from '../layer-type-selector/layer-type-selector.component';
import * as MarksHelpers from '../marks-helpers';

@Component({
  standalone: true,
  imports: [
    BaselineChannelComponent,
    ChannelComponent,
    ColorChannelComponent,
    CommonModule,
    LayerTypeSelectorComponent,
    TextChannelComponent,
  ],
  selector: 'mode-layer-card',
  templateUrl: './layer-card.component.html',
  styleUrls: ['./layer-card.component.scss'],
})
export class LayerCardComponent implements OnChanges {
  @Input() series: EncSeries | undefined;
  @Input() renderModel: RenderDataModel | undefined;
  @Input({ required: true }) encoding!: Encoding;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input({ required: true }) view!: FlamingoTypes.ModeView;
  @Input({ required: true }) chartService!: SelectedChartService;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;
  @Input() queryToken: string | undefined;
  @Input() queryRunToken: string | undefined;
  @Input() vizToken: string | undefined;
  @Input() fieldFormatMap: ChartTypes.FieldFormatMap | undefined;
  @Input() fieldColorMap: ChartTypes.FieldColorMap | undefined;
  @Input() currentColorPaletteToken: string | undefined;
  @Input() format: ChartTypes.ChartFormat | undefined;
  @Input() isAutoOrManualRender = true;
  @Input() binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap | undefined;

  @Output() isValid = new EventEmitter<{ seriesId: string; isValid: boolean }>();

  markType: MarkType | 'multiple' | undefined = undefined;
  channelSummarys: Array<MarksHelpers.ChannelSummary> = [];
  automatic = false;
  allAllowedMarkChannels = AllowedMarkChannels;
  allowedMarkChannels: MarkChannel[] = [];
  summary: MarksSummary | undefined;

  trackByChannelSummary = trackByFactory<MarksHelpers.ChannelSummary>(({ channel }) => channel);

  constructor() {
    this.allAllowedMarkChannels = MarksHelpers.getAllowedMarkChannels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.summary = EncodingHelpers.getSummary(this.encoding);
    this.makeSummaryMarks();
  }

  onLayerOptionsChanged(options: MarkTypeOptions) {
    const encodingResult = EncodingHelpers.updateMarkOptions(this.encoding, options, this.series?.field);
    this.chartService.dispatchEncodingUpdate(encodingResult);
  }

  onMarkTypeChanged(type: MarkType | 'automatic') {
    const newMarkType =
      type === 'automatic' ? EncodingHelpers.generateAutomaticMarkType(this.view, this.encoding) : type;

    const incompatibleFieldIds = this.getIncompatibleFieldIds(newMarkType);

    let newEncoding = this.encoding;
    if (incompatibleFieldIds.length > 0) {
      newEncoding = EncodingHelpers.removeFieldsFromEncoding(newEncoding, incompatibleFieldIds).encoding;
    }

    const series = this.series ? this.series.field : this.encoding.marks.channel === '*' ? '*' : undefined;

    let encodingResult = EncodingHelpers.updateFieldMarkType(
      newEncoding,
      series,
      type === 'automatic' ? null : newMarkType
    );
    if (type === 'automatic') {
      encodingResult = EncodingHelpers.updateAutomaticMarkType(this.view, this.encoding, encodingResult, newMarkType);
    }
    this.chartService.dispatchEncodingUpdate(encodingResult);
  }

  makeSummaryMarks() {
    if (this.encoding == null) {
      return [];
    }
    const schemaModel = SwitchSchemaModel.make(this.model, this.view.name);
    if (this.series != null) {
      // Build the summary marks for this series.
      this.markType = this.series.properties.type.value;
      this.automatic = this.series.properties.type.automatic;
      this.allowedMarkChannels = this.markType != null ? this.allAllowedMarkChannels[this.markType] : [];

      this.channelSummarys = this.allowedMarkChannels.map((channel) => MarksHelpers.makeEmptyChannelSummary(channel));
      // For each mark channel type
      this.allowedMarkChannels.forEach((channel) => {
        // Add each field to the series list
        if (this.series) {
          this.series.properties[channel].values.forEach((field) => {
            const markIndex = this.channelSummarys.findIndex((mark) => mark.channel === channel);
            if (markIndex >= 0) {
              const isValid = schemaModel != null ? this.validateEncField(field, schemaModel) : true;
              this.channelSummarys.splice(markIndex, 1, {
                channel,
                type: 'field',
                isValid,
                field,
                id: field.id,
                fieldIds: field.id != null ? [field.id] : [],
              });
            }
          });
        }
      });
    } else if (this.summary != null) {
      // This is All Layers. Use the MarksSummary
      this.markType = this.summary.type;
      this.automatic = this.summary.automatic;
      if (this.summary.type !== 'multiple') {
        this.allowedMarkChannels = this.allAllowedMarkChannels[this.summary.type];
      } else {
        // In the case of multiple series we need to map the max allowed channels to the summary
        const seriesMarkTypes = this.encoding.marks.series.map((s) => s.properties.type.value);
        this.allowedMarkChannels = this.buildAllowedSeriesChannels(seriesMarkTypes);
      }
      this.channelSummarys = this.allowedMarkChannels.map((channel) => MarksHelpers.makeEmptyChannelSummary(channel));

      // For each mark channel type
      this.allowedMarkChannels.forEach((channel) => {
        // Add each summary field of that type
        this.summary?.[channel].forEach((field: EncField) => {
          const channelIndex = this.channelSummarys.findIndex((mark) => mark.channel === channel);
          if (channelIndex >= 0) {
            const isValid = schemaModel != null ? this.validateEncField(field, schemaModel) : true;
            this.channelSummarys.splice(channelIndex, 1, {
              channel,
              type: 'field',
              field,
              isValid,
              id: field.id,
              fieldIds:
                field.id != null ? (this.summary?.[MarksHelpers.markChannelIdsKey(channel)][field.id] ?? []) : [],
            });
          }
        });
        // Optionally add a multiple placeholder if summary has multiple fields
        if (this.summary?.[MarksHelpers.markChannelIdsKey(channel)].multiple?.length) {
          // only need to show one multiple field even if there are more than one in the encoding
          const channelIndex = this.channelSummarys.findIndex((mark) => mark.channel === channel);

          const fieldIds = this.summary[MarksHelpers.markChannelIdsKey(channel)].multiple;
          const allValid = fieldIds.every((fieldId) => {
            if (schemaModel == null) {
              return true;
            }
            const encField = EncodingHelpers.getEncFieldById(this.encoding, fieldId);
            return encField != null ? this.validateEncField(encField, schemaModel) : true;
          });
          this.channelSummarys.splice(channelIndex, 1, {
            channel,
            type: 'multiple',
            field: undefined,
            isValid: allValid,
            id: fieldIds[0],
            fieldIds,
          });
        }
      });
    }
    const isValid = this.channelSummarys.every((summary) => summary.isValid);
    this.isValid.emit({ seriesId: this.series ? this.series.field : '*', isValid });
  }

  validateEncField(field: EncField, schemaModel: SwitchSchemaModel) {
    const compiledFormula = CompiledField.fromEncField(this.view, field);

    if (!compiledFormula.isValid) {
      return false;
    }

    if (
      compiledFormula.attrName &&
      !EncodingHelpers.validateDuplicateCalcFields(this.model, this.view.name, compiledFormula.attrName)
    ) {
      return false;
    }

    if (field.frame?.type === ExprFrame.Type.Custom && schemaModel) {
      const result = EncodingHelpers.validateEncodingFieldFrame(field, this.encoding, schemaModel);
      if (!result.valid) {
        return false;
      }
    }

    if (compiledFormula.isDiscrete) {
      const encSort = this.encoding.sorts.find((sort) =>
        sort.type === SortType.NESTED
          ? // (Field Level) Nested sorts are by alias
            sort.field === field?.id
          : // (View Level) Everything else is by formula
            sort.source === field?.formula
      );

      let isValid = true;
      switch (encSort?.type) {
        case SortType.FIELD:
        case SortType.NESTED: {
          const errors = EncodingHelpers.validateEncSortField(encSort, schemaModel);
          isValid = !errors.length;
          break;
        }
      }
      return isValid;
    }
    return true;
  }

  getIncompatibleFieldIds(newType: MarkType) {
    const incompatibleChannels = this.getIncompatibleChannels(newType);
    return incompatibleChannels.reduce((fieldIds, channel) => {
      if (this.series != null) {
        return fieldIds.concat(this.series.properties[channel].values.map((field) => field.id ?? ''));
      }
      const summary = this.summary ?? EncodingHelpers.getSummary(this.encoding);

      const channelFields = summary[channel];
      const channelFieldIds = summary[MarksHelpers.markChannelIdsKey(channel)];
      if (channelFields[0]?.id != null) {
        fieldIds.push(...(channelFieldIds[channelFields[0].id] ?? []));
      } else if (channelFieldIds.multiple != null) {
        fieldIds.push(...channelFieldIds.multiple);
      }
      return fieldIds;
    }, [] as string[]);
  }

  getIncompatibleChannels(newType: MarkType) {
    const newAllowed = this.allAllowedMarkChannels[newType];
    if (this.series != null) {
      return this.allowedMarkChannels.filter((channel) => !newAllowed.includes(channel));
    }
    const summary = this.summary ?? EncodingHelpers.getSummary(this.encoding);
    if (summary.type !== 'multiple') {
      return this.allAllowedMarkChannels[summary.type].filter((channel) => !newAllowed.includes(channel));
    } else {
      // In the case of multiple series we need to map the max allowed channels to the summary
      const seriesMarkTypes = this.encoding.marks.series.map((s) => s.properties.type.value);
      const channelSet = new Set<MarkChannel>();
      seriesMarkTypes.forEach((seriesMarkType) => {
        if (seriesMarkType != null) {
          const channels = this.allAllowedMarkChannels[seriesMarkType];
          channels.forEach((c) => channelSet.add(c));
        }
      });
      return Array.from(channelSet).filter((channel) => !newAllowed.includes(channel));
    }
  }

  buildAllowedSeriesChannels(seriesTypes: (MarkType | undefined)[]): MarkChannel[] {
    // build a list of allowable summary mark channels from the intersection
    // of all series' allowed mark channels
    return seriesTypes.reduce((acc: MarkChannel[], curr) => {
      if (!curr) return acc;
      if (acc.length) {
        return acc.filter((x) => this.allAllowedMarkChannels[curr].includes(x));
      }
      return this.allAllowedMarkChannels[curr];
    }, []);
  }
}
